import { useState } from 'react';
import { PlusIcon, TrashIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog';
import { useSaveTrackingNumbersAndMarkAsPickedMutation } from '@/graphql/mutations/saveTrackingNumbersAndMarkAsPicked.generated';
import i18n from '@/utils/i18n';

interface MarkAsPickedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  onSuccess: () => Promise<unknown> | void;
}

export function MarkAsPickedDialog({
  open,
  onOpenChange,
  orderId,
  onSuccess,
}: MarkAsPickedDialogProps) {
  const [trackingNumbers, setTrackingNumbers] = useState<string[]>(['']);
  const [saving, setSaving] = useState(false);
  const [markAsPicked] = useSaveTrackingNumbersAndMarkAsPickedMutation();

  const nonEmpty = trackingNumbers.filter((t) => t.trim() !== '');

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setTrackingNumbers(['']);
    }
    onOpenChange(open);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await markAsPicked({
        variables: { id: orderId, tracking_numbers: nonEmpty },
      });
      await onSuccess();
      toast.success(i18n.t('Order:toastMarkAsPickedSuccess'));
      onOpenChange(false);
    } catch {
      toast.error(i18n.t('Order:toastMarkAsPickedError'));
    } finally {
      setSaving(false);
    }
  };

  const updateField = (index: number, value: string) => {
    setTrackingNumbers((prev) => prev.map((v, i) => (i === index ? value : v)));
  };

  const addField = () => setTrackingNumbers((prev) => [...prev, '']);

  const removeField = (index: number) => {
    setTrackingNumbers((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{i18n.t('Order:markAsPickedTitle')}</DialogTitle>
          <DialogDescription>
            {i18n.t('Order:markAsPickedDescription')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="tw:flex tw:flex-col tw:gap-3">
          {trackingNumbers.map((value, index) => (
            <div key={index} className="tw:flex tw:items-center tw:gap-2">
              <Input
                value={value}
                onChange={(e) => updateField(index, e.target.value)}
                placeholder={i18n.t('Order:trackingNumberPlaceholder')}
              />
              {trackingNumbers.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => removeField(index)}
                >
                  <TrashIcon className="tw:size-4" />
                </Button>
              )}
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="tw:self-start"
            onClick={addField}
          >
            <PlusIcon className="tw:size-4" />
            {i18n.t('Common:add')}
          </Button>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{i18n.t('Common:cancel')}</Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Spinner />}
            {i18n.t('Common:save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
